'use client'

import { toHomePage } from '@/feature/account/services/account.routing'
import { useSiteConfig } from '@/feature/config/hooks/useSiteConfig.client'
import { PICKUP_POINT_OF_SALE_ID_COOKIE } from '@/feature/point-of-sale/services/pointOfSale.constants'
import { toQuickOrder } from '@/feature/quick-order/services/quick-order.routing'
import { APOLLO_CLIENT_SIDE_OPTIONS } from '@/lib/apollo/clients/apolloClient.client'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useRouter } from '@/lib/navigation/PageNavigation.client'
import { Drawer, DrawerAction, DrawerBody } from '@/ui/drawer/drawer/Drawer.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { useQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import { MiniCartDocument } from '@gql-generated/graphql'
import { useReactiveGetCookie } from 'cookies-next'
import { useAtom } from 'jotai'
import { useCallback, useEffect } from 'react'
import { isLoggedInCartVisibleAtom } from '../../services/cart.store'
import { MiniCart } from '../mini-cart/MiniCart.client'

gql(/* GraphQL */ `
  query MiniCart {
    currentUser {
      checkout {
        cart(forceRefresh: false) {
          ...MiniCart_Cart
          ...CartSummaryPricing_Cart
        }
      }
    }
  }
`)

export function SignedInCartDrawer() {
  const router = useRouter()
  const { t } = useTranslation(['signedInCartDrawer'])
  const isAuthenticated = useIsAuthenticated()
  const getCookie = useReactiveGetCookie()
  const pickupPointOfSaleId = getCookie(PICKUP_POINT_OF_SALE_ID_COOKIE)

  const {
    data,
    refetch: refetchMiniCartItems,
    loading,
  } = useQuery(MiniCartDocument, {
    ...APOLLO_CLIENT_SIDE_OPTIONS,
    skip: !isAuthenticated || !pickupPointOfSaleId || typeof window === 'undefined',
  })

  const [isDrawerOpen, setIsDrawerOpen] = useAtom(isLoggedInCartVisibleAtom)
  const cart = data?.currentUser?.checkout?.cart
  const items = cart?.items
  const isCartEmpty = !items || items.length === 0 || !items[0]
  const { config } = useSiteConfig()

  const handleOnOpenChange = useCallback(
    (isOpen: boolean) => {
      setIsDrawerOpen(isOpen)
    },
    [setIsDrawerOpen],
  )

  const handleNavigateToQuickOrder = useCallback(() => {
    router.pushWithProgressBar(toQuickOrder)
  }, [router])

  const handleNavigateToHomePage = useCallback(() => {
    router.pushWithProgressBar(toHomePage)
  }, [router])

  useEffect(() => {
    if (isDrawerOpen) {
      refetchMiniCartItems()
    }
  }, [isDrawerOpen, refetchMiniCartItems])

  if (loading) {
    return null
  }

  if (isCartEmpty) {
    return (
      <Drawer
        isOpen={isDrawerOpen}
        onOpenChange={handleOnOpenChange}
        title={t('signedInCartDrawer:title')}
      >
        <DrawerBody>
          <p>{t('signedInCartDrawer:sub')}</p>
        </DrawerBody>
        <DrawerAction
          type="custom"
          handle="continue-shopping"
          variant="secondary"
          action={handleNavigateToHomePage}
        >
          {t('signedInCartDrawer:continue')}
        </DrawerAction>
        {config?.sales?.isQuickOrderEnabled && (
          <DrawerAction
            type="custom"
            handle="quick-order"
            variant="primary"
            icon={<Icon.FastForward />}
            action={handleNavigateToQuickOrder}
          >
            {t('signedInCartDrawer:fastOrder')}
          </DrawerAction>
        )}
      </Drawer>
    )
  }

  return (
    <MiniCart
      isDrawerOpen={isDrawerOpen}
      onOpenChangeAction={handleOnOpenChange}
      items={items?.filter((item) => !!item) ?? []}
      totalQuantity={cart?.totalQuantity ?? 0}
    />
  )
}
